import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import nacmeLogo from './assets/nacme_logo.jpg';
import tlaLogo from './assets/tla_logo.png';
import { IS_TLA, PATHS } from './utils/platformPaths';

const AlumniEmailVerification = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState('verifying'); // 'verifying', 'success', 'error', 'already_verified'
  const [message, setMessage] = useState('');
  const [isResending, setIsResending] = useState(false);
  const [resendEmail, setResendEmail] = useState('');

  useEffect(() => {
    if (token) {
      verifyEmail();
    } else {
      setStatus('error');
      setMessage('Please verify your email address.');
    }
  }, [token]);

  const verifyEmail = async () => {
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';
      const response = await fetch(`${backendUrl}/api/alumni/verify-email/${token}`, {
        method: 'POST'
      });

      const data = await response.json();

      if (response.ok) {
        if (data.message === 'Email already verified') {
          setStatus('already_verified');
          setMessage('Your email has already been verified. You can now log in to your account.');
        } else {
          setStatus('success');
          setMessage('Your email has been verified successfully! You can now log in to your account.');
        }
      } else {
        setStatus('error');
        setMessage('Please verify your email address.');
      }
    } catch (error) {
      console.error('Verification error:', error);
      setStatus('error');
      setMessage('Please verify your email address.');
    }
  };

  const handleResendVerification = async (e) => {
    e.preventDefault();
    if (!resendEmail.trim()) {
      alert('Please enter your email address');
      return;
    }

    setIsResending(true);
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';
      const response = await fetch(`${backendUrl}/api/alumni/resend-verification`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ email: resendEmail })
      });

      const data = await response.json();

      if (response.ok) {
        alert('Verification email sent successfully! Please check your inbox.');
        setResendEmail('');
      } else {
        alert(data.detail || 'Failed to send verification email');
      }
    } catch (error) {
      console.error('Resend error:', error);
      alert('An error occurred while sending the verification email');
    } finally {
      setIsResending(false);
    }
  };

  const getStatusIcon = () => {
    switch (status) {
      case 'verifying':
        return (
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
        );
      case 'success':
      case 'already_verified':
        return (
          <svg className="h-12 w-12 text-green-500 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        );
      case 'error':
        return (
          <svg className="h-12 w-12 text-red-500 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        );
      default:
        return null;
    }
  };

  const getStatusColor = () => {
    switch (status) {
      case 'success':
      case 'already_verified':
        return 'text-green-600';
      case 'error':
        return 'text-red-600';
      default:
        return 'text-gray-600';
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col justify-center py-6 px-4 sm:py-12 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <img className={`mx-auto w-auto ${IS_TLA ? 'h-48 sm:h-60' : 'h-16 sm:h-20'}`} src={IS_TLA ? tlaLogo : nacmeLogo} alt={IS_TLA ? "TLA Logo" : "NACME Logo"} />
        <h2 className="mt-4 sm:mt-6 text-center text-2xl sm:text-3xl font-extrabold text-gray-900">
          Email Verification
        </h2>
      </div>

      <div className="mt-6 sm:mt-8 mx-auto w-full max-w-sm sm:max-w-md">
        <div className="bg-white py-6 px-4 shadow-md rounded-lg sm:py-8 sm:px-10 sm:shadow-lg">
          <div className="text-center">
            {getStatusIcon()}
            
            <h3 className={`mt-4 text-base sm:text-lg font-medium ${getStatusColor()}`}>
              {status === 'verifying' && 'Verifying your email...'}
              {status === 'success' && 'Email Verified!'}
              {status === 'already_verified' && 'Already Verified'}
              {status === 'error' && 'Unverified'}
            </h3>
            
            <p className="mt-2 text-sm sm:text-base text-gray-600 px-2">
              {message}
            </p>

            <div className="mt-6">
              {(status === 'success' || status === 'already_verified') && (
                <Link
                  to={PATHS.login}
                  className="w-full flex justify-center py-3 sm:py-2 px-4 border border-transparent rounded-md shadow-sm text-base sm:text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  Go to Login
                </Link>
              )}

              {status === 'error' && (
                <div className="space-y-4">
                  <div className="border-t border-gray-200 pt-4">
                    <p className="text-sm sm:text-base text-gray-600 mb-3">
                      Already have an account? Resend verification email:
                    </p>
                    <form onSubmit={handleResendVerification} className="space-y-3">
                      <input
                        type="email"
                        placeholder="Enter your email address"
                        value={resendEmail}
                        onChange={(e) => setResendEmail(e.target.value)}
                        className="w-full px-3 py-3 sm:py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500 text-base sm:text-sm"
                        required
                      />
                      <button
                        type="submit"
                        disabled={isResending}
                        className="w-full flex justify-center py-3 sm:py-2 px-4 border border-transparent rounded-md shadow-sm text-base sm:text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
                      >
                        {isResending ? 'Sending...' : 'Resend Verification Email'}
                      </button>
                    </form>
                  </div>
                  
                  <div className="border-t border-gray-200 pt-4">
                    <Link
                      to={PATHS.register}
                      className="w-full flex justify-center py-3 sm:py-2 px-4 border border-gray-300 rounded-md shadow-sm text-base sm:text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                    >
                      Register New Account
                    </Link>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AlumniEmailVerification;